import { useEffect, useRef, useState, type ReactNode } from 'react'
import clsx from 'clsx'

interface Props {
  children: ReactNode
  /** Stagger, in milliseconds, for items revealed as a group. */
  delay?: number
  className?: string
}

/**
 * Settles its children into view the first time they are scrolled to.
 *
 * <p>Reveals once and then stops observing: content that fades out again on
 * the way back up is distracting rather than polished.
 *
 * <p>If the browser has no IntersectionObserver the content is shown straight
 * away. Hiding it would be a far worse failure than skipping an animation.
 */
export function Reveal({ children, delay = 0, className }: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const [shown, setShown] = useState(false)

  useEffect(() => {
    const element = ref.current
    if (!element) return
    if (typeof IntersectionObserver === 'undefined') {
      setShown(true)
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true)
          observer.disconnect()
        }
      },
      // Fire a little before the element is fully on screen, so the motion has
      // finished by the time the reader reaches it.
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 },
    )
    observer.observe(element)

    // Safety net. Content that is merely un-animated is a small blemish;
    // content that stays invisible because an observer never fired is a blank
    // page. If nothing has triggered by now, show it anyway.
    const failsafe = window.setTimeout(() => setShown(true), 2500)

    return () => {
      observer.disconnect()
      window.clearTimeout(failsafe)
    }
  }, [])

  return (
    <div
      ref={ref}
      className={clsx('reveal', shown && 'reveal-in', className)}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </div>
  )
}
